import { getSupervisorActionDefinition } from './action-registry'
import { requestSupervisorApproval } from './approval-store'
import { executeSupervisedAction } from './action-executor'
import type {
  SupervisorBridgeRequestMessage,
  SupervisorBridgeResponseMessage,
  SupervisedActionRequest,
  SupervisedActionResponse
} from './types'

function isSupervisorRequestMessage(value: unknown): value is SupervisorBridgeRequestMessage {
  if (!value || typeof value !== 'object') {
    return false
  }

  return (value as SupervisorBridgeRequestMessage).type === 'radsuite.supervisor.request'
}

function buildErrorResponse(request: SupervisedActionRequest | undefined, code: string, message: string): SupervisorBridgeResponseMessage {
  return {
    type: 'radsuite.supervisor.response',
    response: {
      requestId: request?.requestId || 'unknown',
      ok: false,
      status: 'failed',
      error: {
        code,
        message
      }
    }
  }
}

async function handleSupervisorRequest(request: SupervisedActionRequest): Promise<SupervisedActionResponse> {
  const definition = getSupervisorActionDefinition(request.action)

  if (!definition) {
    return {
      requestId: request.requestId,
      ok: false,
      status: 'failed',
      error: {
        code: 'ACTION_NOT_ALLOWED',
        message: `Unsupported supervised action: ${request.action}`
      }
    }
  }

  if (!definition.requiresConfirmation) {
    return executeSupervisedAction(request)
  }

  const approvalResponse = await requestSupervisorApproval(request)
  if (!approvalResponse.ok) {
    return approvalResponse
  }

  return executeSupervisedAction(request)
}

export function initializeSupervisorPostMessageBridge() {
  if (typeof window === 'undefined') return

  window.addEventListener('message', async (event: MessageEvent) => {
    if (!isSupervisorRequestMessage(event.data)) {
      return
    }

    const request = event.data.request

    if (!request?.requestId || !request?.action || !request?.accountId) {
      event.source?.postMessage(buildErrorResponse(request, 'INVALID_REQUEST', 'Supervisor request is missing required fields.'), {
        targetOrigin: event.origin
      })
      return
    }

    const response = await handleSupervisorRequest({
      ...request,
      metadata: {
        ...request.metadata,
        origin: event.origin
      }
    })

    const message: SupervisorBridgeResponseMessage = {
      type: 'radsuite.supervisor.response',
      response
    }

    event.source?.postMessage(message, {
      targetOrigin: event.origin
    })
  })
}
